import { useCallback } from 'react';
import { useScreenData } from './useScreenData';

/**
 * Cascading Timezone Hook
 *
 * Manages the personal timezone preference with the following hierarchy:
 * 1. User-level override, scoped PER-USER-PER-COMPANY
 *    (settings.userTimezoneOverride, persisted via the user settings endpoint)
 * 2. Company/system default (settings.defaultTimezone from admin settings)
 *
 * Data Flow: System Default → User Default → Timeline Dropdown → Booking Form → Saved Booking
 */
export function useTimezone() {
  const { settings, updateUserSettings } = useScreenData('settings');

  const systemDefault = settings.defaultTimezone;
  const override = settings.userTimezoneOverride;
  const hasUserOverride = override != null && override !== '';
  const effectiveTimezone = hasUserOverride ? (override as string) : systemDefault;

  // Set the user-level (per-company) timezone override.
  const setUserTimezone = useCallback(async (timezone: string) => {
    await updateUserSettings({ userTimezoneOverride: timezone });
  }, [updateUserSettings]);

  // Clear the override and revert to the company/system default.
  const clearUserTimezone = useCallback(async () => {
    await updateUserSettings({ userTimezoneOverride: null });
  }, [updateUserSettings]);

  return {
    // Current effective timezone (respects cascade)
    timezone: effectiveTimezone,

    // Set user-level (per-company) timezone override
    setUserTimezone,

    // Clear user override
    clearUserTimezone,

    // Check if user has override
    hasUserOverride,

    // Get system/company default
    systemDefault,

    // Available timezones
    availableTimezones: settings.availableTimezones,

    // Whether timezones are enabled
    enabled: settings.enableTimezones,
  };
}
